import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ArrowRight, FileText, Send, List } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HomePage = () => {
  const navigate = useNavigate();

  const modules = [
    {
      id: 'inward',
      title: 'Inward Entry',
      description: 'Record incoming goods and materials',
      icon: FileText,
      route: '/gate-entry/inward',
      color: 'bg-[#C8E6C9]',
      textColor: 'text-[#1B4D1F]',
      available: true
    },
    {
      id: 'my-entries',
      title: 'My Entries',
      description: 'View submissions & sync status',
      icon: List,
      route: '/my-entries',
      color: 'bg-[#FFE4B5]',
      textColor: 'text-[#5C3D0A]',
      available: true
    },
    {
      id: 'documentation',
      title: 'Inward Documentation',
      description: 'AI-powered document data extraction',
      icon: FileText,
      route: '/documentation',
      color: 'bg-[#B8D4E8]',
      textColor: 'text-[#1A3A4F]',
      available: true
    },
    {
      id: 'print-codes',
      title: 'Print Codes',
      description: 'Generate & print barcodes/QR codes',
      icon: Package,
      route: '/print-codes',
      color: 'bg-[#E8D9F3]',
      textColor: 'text-[#3D1F5C]',
      available: true
    },
    {
      id: 'outward-jobworker',
      title: 'Outward (Job Worker)',
      description: 'Dispatch to job workers',
      icon: Send,
      route: '/gate-entry/outward-jobworker',
      color: 'bg-[#F5D5D8]',
      textColor: 'text-[#6B1B23]',
      available: false
    },
    {
      id: 'outward-customer',
      title: 'Outward (Customer)',
      description: 'Dispatch to customers',
      icon: Send,
      route: '/gate-entry/outward-customer',
      color: 'bg-[#C8E6C9]',
      textColor: 'text-[#1B4D1F]',
      available: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold font-display">WarehouseOps Pro</h1>
              <p className="text-sm text-[#E8DCC8]">Enterprise Warehouse Management System</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#1A1F2E] mb-2 font-display">Gate Entry Module</h2>
          <p className="text-[#5A5F6E]">Select an operation to continue</p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <div
                key={module.id}
                className={`bg-white rounded-lg border-2 border-[#E5DFD0] shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${
                  module.available ? 'hover:border-[#2D5F4F] cursor-pointer' : 'opacity-60'
                }`}
                onClick={() => module.available && navigate(module.route)}
                data-testid={`module-card-${module.id}`}
              >
                <div className={`${module.color} p-6`}>
                  <Icon className={`w-12 h-12 ${module.textColor}`} />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1A1F2E] mb-2 font-display flex items-center justify-between">
                    {module.title}
                    {module.available && <ArrowRight className="w-5 h-5 text-[#2D5F4F]" />}
                  </h3>
                  <p className="text-[#5A5F6E] text-sm mb-4">{module.description}</p>
                  {module.available ? (
                    <Button
                      className="w-full bg-[#2D5F4F] hover:bg-[#234A3D] text-white h-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(module.route);
                      }}
                      data-testid={`${module.id}-button`}
                    >
                      Open Module
                    </Button>
                  ) : (
                    <div className="bg-[#F5F1E8] rounded-lg px-4 py-2 text-center">
                      <span className="text-sm text-[#8A8F9E] font-medium">Coming Soon</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-[#1A1F2E] mb-3 font-display">Getting Started</h3>
          <ul className="space-y-2 text-[#5A5F6E]">
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-1">•</span>
              <span>Ensure your barcode scanner is connected and configured</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-1">•</span>
              <span>Mobile users: Grant camera permissions for document capture</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-1">•</span>
              <span>All entries are timestamped in IST (Indian Standard Time)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2D5F4F] mt-1">•</span>
              <span>Maximum 10 images can be captured per entry</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default HomePage;